import { lazy } from 'react';

/**
 * Route-level `lazy()` page chunks (A23). Extracted from `routes.tsx` so that
 * file stays a readable route TREE under the ~300-line ceiling — this module is
 * the flat page-import registry it consumes.
 *
 * Every page is a `lazy()` chunk so the eager bundle carries only the front door
 * (signin / redirects / 404), the auth guard, and the shell chrome. Heavy deps
 * (recharts, Paddle, the Design Studio stack) ride their page's chunk instead of
 * the first paint. Pages export named symbols, so each lazy maps the named
 * export to `default`.
 */
// The Analytics hub — "is the agent working, in business terms", in four rooms.
// It ABSORBED `/impact` (Business Success Visibility § 2.4 / D-2): the impact
// page's panels are all still here, composed into the hub's rooms rather than
// standing as an operator-only page of their own, and `/impact` redirects.
export const AnalyticsPage = lazy(() =>
  import('@/features/analytics/AnalyticsPage').then((m) => ({
    default: m.AnalyticsPage,
  })),
);

// C-12 — the public shared Agent-Impact report page (unauthenticated, outside
// AuthGuard/AppShell), reached by a forwarded `/shared/impact/:token` link.
export const SharedReportPage = lazy(() =>
  import('@/features/shared-report').then((m) => ({
    default: m.SharedReportPage,
  })),
);

// REP-1b — the public saved report a workspace forwards, reached by a
// `/shared/report/:token` link. Unauthenticated and outside AuthGuard/AppShell,
// the C-12 page's twin one level along.
export const SharedSavedReportPage = lazy(() =>
  import('@/features/shared-saved-report').then((m) => ({
    default: m.SharedSavedReportPage,
  })),
);

// C-12 — the public no-login unsubscribe confirmation for the weekly report email.
export const UnsubscribePage = lazy(() =>
  import('@/features/shared-report').then((m) => ({
    default: m.UnsubscribePage,
  })),
);

// Imported by FILE, not via a barrel: Vite names a chunk after its entry
// module's basename, so a barrel import ships as an anonymous `index-*.js` that
// no size-limit glob can pin. `/map` carries a bundled layout engine and a
// size budget that has to be enforceable, so the chunk gets a real name.
export const LimitsPage = lazy(() =>
  import('@/features/limits/LimitsPage').then((m) => ({ default: m.LimitsPage })),
);
export const MapPage = lazy(() =>
  import('@/features/map/MapPage/MapPage').then((m) => ({
    default: m.MapPage,
  })),
);

export const OverviewPage = lazy(() =>
  import('@/features/overview/OverviewPage').then((m) => ({ default: m.OverviewPage })),
);
// DE-301 § 7.2 — the documentation hub is five ROUTES under one layout, not a
// `?tab=` page. `DocsHubPage` is gone; `legacy-tab-redirect.ts` forwards its
// bookmarks.
export const DocsLayout = lazy(() =>
  import('@/features/docs/DocsLayout').then((m) => ({ default: m.DocsLayout })),
);
export const DocsOverviewPage = lazy(() =>
  import('@/features/docs/DocsOverviewPage').then((m) => ({ default: m.DocsOverviewPage })),
);
export const DocsArticlesPage = lazy(() =>
  import('@/features/docs/DocsArticlesPage').then((m) => ({ default: m.DocsArticlesPage })),
);
export const DocsTeamPage = lazy(() =>
  import('@/features/docs/DocsTeamPage').then((m) => ({ default: m.DocsTeamPage })),
);
export const DocsGapsPage = lazy(() =>
  import('@/features/docs/DocsGapsPage').then((m) => ({ default: m.DocsGapsPage })),
);
export const DocsSourcesView = lazy(() =>
  import('@/features/docs/DocsSourcesView').then((m) => ({ default: m.DocsSourcesView })),
);
export const DocsSettingsPage = lazy(() =>
  import('@/features/docs/DocsSettingsPage').then((m) => ({ default: m.DocsSettingsPage })),
);
export const LegacySourceRedirect = lazy(() =>
  import('@/features/docs/DocsLayout/LegacySourceRedirect').then((m) => ({
    default: m.LegacySourceRedirect,
  })),
);
export const WalkApprovalPage = lazy(() =>
  import('@/features/catalog/WalkApprovalPage').then((m) => ({ default: m.WalkApprovalPage })),
);
export const CapabilityDetailPage = lazy(() =>
  import('@/features/catalog/CapabilityDetailPage').then((m) => ({
    default: m.CapabilityDetailPage,
  })),
);
export const PromptsListPage = lazy(() =>
  import('@/features/prompts/PromptsListPage').then((m) => ({ default: m.PromptsListPage })),
);
export const PromptsComparePage = lazy(() =>
  import('@/features/prompts/ComparePage').then((m) => ({ default: m.ComparePage })),
);
export const PromptsResolvePage = lazy(() =>
  import('@/features/prompts/ResolvePage').then((m) => ({ default: m.ResolvePage })),
);
export const PromptProfileRoute = lazy(() =>
  import('@/features/prompts/PromptProfileRoute').then((m) => ({ default: m.PromptProfileRoute })),
);
export const ConnectAppPage = lazy(() =>
  import('@/features/connect').then((m) => ({ default: m.ConnectAppPage })),
);
export const TeamPage = lazy(() =>
  import('@/features/team/TeamPage').then((m) => ({ default: m.TeamPage })),
);
export const DocInspectorPage = lazy(() =>
  import('@/features/docs/DocInspectorPage').then((m) => ({ default: m.DocInspectorPage })),
);
export const MonitoringPage = lazy(() =>
  import('@/features/monitoring/MonitoringPage').then((m) => ({ default: m.MonitoringPage })),
);
export const SystemLogsPage = lazy(() =>
  import('@/features/system-logs').then((m) => ({ default: m.SystemLogsPage })),
);
export const TaskGuidesPage = lazy(() =>
  import('@/features/task-guides/TaskGuidesPage').then((m) => ({
    default: m.TaskGuidesPage,
  })),
);

export const FunnelsPage = lazy(() =>
  import('@/features/funnels/FunnelsPage').then((m) => ({
    default: m.FunnelsPage,
  })),
);

export const SavedReportsPage = lazy(() =>
  import('@/features/saved-reports/SavedReportsPage').then((m) => ({
    default: m.SavedReportsPage,
  })),
);
export const WorkspaceSettingsPage = lazy(() =>
  import('@/features/workspace-settings/WorkspaceSettingsPage').then((m) => ({
    default: m.WorkspaceSettingsPage,
  })),
);
export const AllowedSitesPage = lazy(() =>
  import('@/features/allowed-sites/AllowedSitesPage').then((m) => ({
    default: m.AllowedSitesPage,
  })),
);
export const ApiKeysPage = lazy(() =>
  import('@/features/api-keys/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })),
);
export const ExperimentalFeaturesPage = lazy(() =>
  import('@/features/experimental-features/ExperimentalFeaturesPage').then((m) => ({
    default: m.ExperimentalFeaturesPage,
  })),
);
export const AccountPage = lazy(() =>
  import('@/features/account').then((m) => ({ default: m.AccountPage })),
);
export const TeachModePage = lazy(() =>
  import('@/features/teach-mode').then((m) => ({ default: m.TeachModePage })),
);
export const SiteWalkPage = lazy(() =>
  import('@/features/site-walk').then((m) => ({ default: m.SiteWalkPage })),
);
export const SiteWalkRunPage = lazy(() =>
  import('@/features/site-walk').then((m) => ({ default: m.SiteWalkRunPage })),
);
export const StudioPage = lazy(() =>
  import('@/features/widget-design-studio/StudioPage').then((m) => ({ default: m.StudioPage })),
);
export const DesignProfilesPage = lazy(() =>
  import('@/features/design-profiles').then((m) => ({ default: m.DesignProfilesPage })),
);
export const PlaygroundPage = lazy(() =>
  import('@/features/playground').then((m) => ({ default: m.PlaygroundPage })),
);
// `/engagement` — the merged surface: openers, nudges, placement, and the rule
// board. Its rule editor below is a separate export from `/page-rules`' editor,
// which is a different surface entirely.
export const EngagementPage = lazy(() =>
  import('@/features/engagement').then((m) => ({ default: m.EngagementPage })),
);
export const EngagementRuleEditorPage = lazy(() =>
  import('@/features/engagement').then((m) => ({ default: m.RuleEditorPage })),
);
// On-page experiences Phase B — `/experiences`, the authoring front door for
// experience units. Its own chunk: the runtime shipped before the screen did, so
// a workspace that never opens this page must not pay for the editor's bytes.
export const ExperiencesPage = lazy(() =>
  import('@/features/experiences').then((m) => ({ default: m.ExperiencesPage })),
);
export const ExperienceEditorPage = lazy(() =>
  import('@/features/experiences').then((m) => ({ default: m.ExperienceEditorPage })),
);
// The saved-guide library — the referent behind the `guideId` a unit's
// `start-guide` CTA and `guide-completed` goal name. Same chunk as the units
// surface: an operator writing a guide is on their way to pointing a banner at
// it, so the two are one visit.
export const ExperienceGuidesPage = lazy(() =>
  import('@/features/experiences').then((m) => ({ default: m.GuidesPage })),
);
export const ChatExperiencePage = lazy(() =>
  import('@/features/chat-experience').then((m) => ({ default: m.ChatExperiencePage })),
);

export const ToolsCatalogPage = lazy(() =>
  import('@/features/tools-catalog').then((m) => ({ default: m.ToolsCatalogPage })),
);
export const ConnectionsPage = lazy(() =>
  import('@/features/connections').then((m) => ({ default: m.ConnectionsPage })),
);
export const RuleEditorPage = lazy(() =>
  import('@/features/page-rules').then((m) => ({ default: m.RuleEditorPage })),
);
export const ScanPage = lazy(() =>
  import('@/features/scan').then((m) => ({ default: m.ScanPage })),
);
export const OAuthConsentPage = lazy(() =>
  import('@/features/oauth-consent').then((m) => ({ default: m.OAuthConsentPage })),
);
export const BillingPage = lazy(() =>
  import('@/features/billing/BillingPage').then((m) => ({ default: m.BillingPage })),
);
export const ConversationLogsPage = lazy(() =>
  import('@/features/conversation-logs/ConversationLogsPage').then((m) => ({
    default: m.ConversationLogsPage,
  })),
);
export const ConversationTranscriptPage = lazy(() =>
  import('@/features/conversation-logs/ConversationTranscriptPage').then((m) => ({
    default: m.ConversationTranscriptPage,
  })),
);
export const AudienceDetailPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.AudienceDetailPage })),
);
export const UserDetailPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UserDetailPage })),
);
// UGL-105 A0 — the routed Users surface: a layout + four sub-pages replace the
// pre-split `?tab=` `UserLearningPage`.
export const UsersLayout = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UsersLayout })),
);
export const UsersRosterPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UsersRosterPage })),
);
export const AudiencesPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.AudiencesPage })),
);
export const UsersInsightsPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UsersInsightsPage })),
);
export const UsersSettingsPage = lazy(() =>
  import('@/features/users').then((m) => ({ default: m.UsersSettingsPage })),
);
export const CreateWorkspacePage = lazy(() =>
  import('@/features/onboarding/CreateWorkspacePage/CreateWorkspacePage').then((m) => ({
    default: m.CreateWorkspacePage,
  })),
);
export const CliAuthPage = lazy(() =>
  import('@/features/cli-auth/CliAuthPage').then((m) => ({ default: m.CliAuthPage })),
);
export const InviteAcceptPage = lazy(() =>
  import('@/features/team/InviteAcceptPage').then((m) => ({ default: m.InviteAcceptPage })),
);
export const CheckoutPage = lazy(() =>
  import('@/features/checkout/CheckoutPage').then((m) => ({ default: m.CheckoutPage })),
);
